import type { Dispatch, MouseEvent, ReactElement, SetStateAction } from "react";
import { useEffect, useMemo, useRef, useState } from "react";

import { colorsPalettes } from "@similarweb/styles";
import { Dropdown, EllipsisDropdownItem } from "@similarweb/ui-components";

import { translate } from "app/filters/i18nFilter";
import {
    COMPETITORS_POPUP_HEIGHT,
    COMPETITORS_POPUP_WIDTH,
    DESCRIPTION_ID,
    MAX_COMPETITORS,
} from "app/pages/activation/components/RankWidget/config";
import { DropdownButton } from "app/pages/activation/components/RankWidget/CreationModal/Components/DropdownButton/DropdownButton";
import { modalsTexts } from "app/pages/activation/components/RankWidget/texts";
import type { RTFormData } from "app/pages/activation/components/RankWidget/types";
import { useActivationProjectContext } from "app/pages/activation/context/Project/ProjectContext";
import { activationTracking } from "app/pages/activation/services/tracking";

import { Footer } from "./Footer";
import { getDropdownIds } from "./helpers";

type CompetitorsDropdownSchema = {
    competitors: string[];
    setFormData?: Dispatch<SetStateAction<RTFormData>>;
    dataAutomation?: string;
};

export const CompetitorsDropdown = ({ competitors, setFormData, dataAutomation }: CompetitorsDropdownSchema) => {
    const dropdownRef = useRef(null);
    const { favicons } = useActivationProjectContext();
    const [selectedCompetitors, setSelectedCompetitors] = useState<string[]>([]);
    const { rankTracker } = activationTracking;

    const isSomeItemSelected = !!selectedCompetitors?.length;
    const isMaxSelected = selectedCompetitors.length >= MAX_COMPETITORS;

    useEffect(() => {
        if (competitors?.length <= MAX_COMPETITORS) {
            setSelectedCompetitors(competitors);

            return;
        }

        setSelectedCompetitors(competitors.slice(0, MAX_COMPETITORS));
    }, [competitors]);

    useEffect(() => {
        setFormData?.((prevState) => ({ ...prevState, competitors: selectedCompetitors }));
    }, [selectedCompetitors]);

    const onCompetitorSelect = (id: string) => {
        const isAlreadySelected = selectedCompetitors.includes(id);

        if (isAlreadySelected) {
            setSelectedCompetitors((competitors: string[]) => competitors.filter((domain: string) => id !== domain));

            return;
        }

        if (!isMaxSelected) {
            setSelectedCompetitors((state) => [id, ...state]);
        }
    };

    const getItems = useMemo(
        () =>
            competitors
                ? competitors.map((item: string) => {
                      const isSelected = selectedCompetitors.includes(item);
                      const disabled = isMaxSelected && !isSelected;

                      return (
                          <EllipsisDropdownItem
                              imageUrl={favicons[item]}
                              key={item}
                              id={item}
                              text={item}
                              selected={isSelected}
                              showCheckBox
                              hasBorderedIcons
                              disabled={disabled}
                          >
                              {item}
                          </EllipsisDropdownItem>
                      );
                  })
                : [],
        [competitors, favicons, selectedCompetitors, isMaxSelected],
    );

    const onClick = ({ id }: { id: string }) => {
        onCompetitorSelect(id);
    };

    const onApply = (event: MouseEvent<HTMLDivElement>) => {
        event.stopPropagation();

        dropdownRef.current?.closePopup();
    };

    const onToggle = (isOpen: boolean) => {
        if (!isOpen) {
            rankTracker.trackChangeCompetitors(selectedCompetitors.length);
        }
    };

    const searchFilter = (term: string, item: ReactElement): boolean => {
        return item.key.toLowerCase().startsWith(term.toLowerCase());
    };

    const dropdownIds = getDropdownIds(selectedCompetitors, competitors, false);

    return (
        <Dropdown
            ref={dropdownRef}
            width={COMPETITORS_POPUP_WIDTH}
            dropdownPopupHeight={COMPETITORS_POPUP_HEIGHT}
            appendTo={`#${DESCRIPTION_ID}`}
            dropdownPopupPlacement="bottom-left"
            onClick={onClick}
            searchFilter={searchFilter}
            closeOnItemClick={false}
            selectedIds={dropdownIds}
            footerComponent={() => (
                <Footer
                    onApply={onApply}
                    isMaxSelected={isMaxSelected}
                    isSomeSelected={isSomeItemSelected}
                />
            )}
            hasSearch
            searchPlaceHolder={translate(modalsTexts.competitorsDropdownSearchPlaceholder)}
            buttonWidth="max-content"
            onToggle={onToggle}
        >
            {[
                <DropdownButton
                    dataAutomation={dataAutomation}
                    color={colorsPalettes.carbon[400]}
                    key="competitors-dropdown"
                >
                    {translate(modalsTexts.competitorsDropdownButtonText, { count: selectedCompetitors.length })}
                </DropdownButton>,
                ...getItems,
            ]}
        </Dropdown>
    );
};
